import { useEffect, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { ACCENT } from "./landingTheme";

// Nav das páginas públicas (antes do login). Vive fora do index.js da landing
// porque serve a landing e a página de podcast — uma cópia em cada sairia de
// sincronia no primeiro item de menu novo.

/*
 * Menu principal. Lista em vez de links soltos no JSX: o próximo item entra
 * aqui, não no meio da marcação.
 *
 * `hash` leva a uma seção da landing; `to` leva a uma página. A distinção
 * importa porque âncora precisa de tratamento próprio quando o visitante já
 * está em outra página (ver `irPara`).
 */
const MENU = [
  { label: "Emissoras", hash: "emissoras" },
  { label: "Podcast", to: "/podcast" },
  { label: "Recursos", hash: "laminas" },
  { label: "Clientes", hash: "clientes" },
  { label: "Planos", to: "/planos" },
];

// A barra é transparente sobre o topo da página e ganha fundo ao rolar. O
// gatilho é baixo de propósito: basta sair do topo para o texto do menu
// deixar de disputar com o conteúdo que passa por baixo.
const SCROLL_PARA_FUNDO = 24;

const linkBase = {
  textDecoration: "none",
  fontSize: "0.875rem",
  fontWeight: 400,
  padding: "0.45rem 0.85rem",
  borderRadius: 8,
  letterSpacing: "0.01em",
  whiteSpace: "nowrap",
  transition: "color 0.18s, background 0.18s",
};

export default function LandingNav() {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const [rolou, setRolou] = useState(false);
  const [menuAberto, setMenuAberto] = useState(false);

  useEffect(() => {
    const aoRolar = () => setRolou(window.scrollY > SCROLL_PARA_FUNDO);
    aoRolar();
    window.addEventListener("scroll", aoRolar, { passive: true });
    return () => window.removeEventListener("scroll", aoRolar);
  }, []);

  // Fecha o menu do celular ao trocar de página — senão ele fica aberto por
  // cima do conteúdo novo.
  useEffect(() => setMenuAberto(false), [pathname, hash]);

  /*
   * Âncora funciona de duas formas: estando na landing, rola na hora; vindo da
   * página de podcast, navega para "/" com o hash e deixa a landing rolar
   * depois de montar (ver o efeito de hash em Landing/index.js). Sem esse
   * ramo, clicar em "Clientes" dentro de /podcast não faria nada.
   */
  const irPara = (item) => (evento) => {
    if (!item.hash) return;
    evento.preventDefault();
    setMenuAberto(false);

    if (pathname === "/") {
      document.getElementById(item.hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    navigate(`/#${item.hash}`);
  };

  const itemAtivo = (item) => (item.to ? pathname === item.to : false);

  // Função em vez de um array pronto: o painel do celular usa os mesmos itens
  // com padding maior, e espalhar um elemento React já criado (`{...el}`) não
  // produz um elemento renderizável.
  const montarLinks = (estiloExtra = {}) =>
    MENU.map((item) => {
      const ativo = itemAtivo(item);
      const estilo = {
        ...linkBase,
        color: ativo ? ACCENT : "rgba(255,255,255,0.72)",
        fontWeight: ativo ? 500 : 400,
        ...estiloExtra,
      };

      return item.hash ? (
        <a key={item.label} href={`/#${item.hash}`} onClick={irPara(item)} className="nav-link" style={estilo}>
          {item.label}
        </a>
      ) : (
        <RouterLink key={item.label} to={item.to} className="nav-link" style={estilo}>
          {item.label}
        </RouterLink>
      );
    });

  return (
    <>
      <style>{`
        .nav-link:hover { color: #fff !important; background: rgba(255,255,255,0.07); }
        .nav-cta:hover { border-color: rgba(255,255,255,0.75) !important; }
        /* Cinco itens + dois botões não cabem num celular: abaixo de 960px o
           menu vira painel sob a barra, acionado pelo hambúrguer. */
        @media (max-width: 960px) {
          .nav-desktop { display: none !important; }
          .nav-hamburguer { display: flex !important; }
        }
      `}</style>

      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          height: 60,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 clamp(1rem, 4vw, 2rem)",
          // O fundo só aparece depois de sair do topo. `backdrop-filter` deixa
          // o conteúdo passar borrado por baixo em vez de sumir atrás de uma
          // faixa opaca.
          background: rolou ? "rgba(10,10,10,0.82)" : "transparent",
          backdropFilter: rolou ? "saturate(160%) blur(12px)" : "none",
          WebkitBackdropFilter: rolou ? "saturate(160%) blur(12px)" : "none",
          borderBottom: rolou ? "1px solid rgba(255,255,255,0.08)" : "1px solid transparent",
          transition: "background 0.25s ease, border-color 0.25s ease",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "1.75rem", minWidth: 0 }}>
          <RouterLink to="/" style={{ display: "flex", alignItems: "center", textDecoration: "none", flexShrink: 0 }}>
            <img src="/static/images/logo/logoyouon.svg" alt="Youon" style={{ height: 26 }} />
          </RouterLink>

          <div className="nav-desktop" style={{ display: "flex", alignItems: "center", gap: "0.15rem" }}>
            {montarLinks()}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <RouterLink
            to="/account/login"
            className="nav-link nav-desktop"
            style={{ ...linkBase, color: "rgba(255,255,255,0.72)" }}
          >
            Entrar
          </RouterLink>
          <RouterLink
            to="/account/register"
            className="nav-cta"
            style={{
              ...linkBase,
              color: "#0a0a0a",
              background: ACCENT,
              fontWeight: 600,
              padding: "0.5rem 1.1rem",
              borderRadius: 9999,
              border: "1px solid transparent",
            }}
          >
            Criar conta
          </RouterLink>

          {/* Hambúrguer — só abaixo de 960px, ver o media query acima. */}
          <button
            className="nav-hamburguer"
            onClick={() => setMenuAberto((v) => !v)}
            aria-label={menuAberto ? "Fechar menu" : "Abrir menu"}
            aria-expanded={menuAberto}
            style={{
              display: "none",
              alignItems: "center",
              justifyContent: "center",
              width: 38,
              height: 38,
              marginLeft: "0.25rem",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.16)",
              background: "transparent",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
              {menuAberto ? (
                <>
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </>
              ) : (
                <>
                  <path d="M3 6h18" />
                  <path d="M3 12h18" />
                  <path d="M3 18h18" />
                </>
              )}
            </svg>
          </button>
        </div>
      </nav>

      {menuAberto && (
        <div
          style={{
            position: "fixed",
            top: 60,
            left: 0,
            right: 0,
            zIndex: 99,
            background: "rgba(10,10,10,0.97)",
            backdropFilter: "blur(12px)",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            padding: "0.75rem clamp(1rem, 4vw, 2rem) 1.25rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.15rem",
          }}
        >
          {montarLinks({ padding: "0.7rem 0.6rem" })}
          <RouterLink
            to="/account/login"
            className="nav-link"
            style={{ ...linkBase, color: "rgba(255,255,255,0.72)", padding: "0.7rem 0.6rem" }}
          >
            Entrar
          </RouterLink>
        </div>
      )}
    </>
  );
}
